// src/pages/lab/processing/SampleProcessingPage.tsx
import React, { useEffect, useState } from "react";
import { Tabs } from "antd";

// Components
import Layout from "../../../components/Layout";
import { PageContainer, SectionCard, InlineLoading } from "../../../components/shared";

// API
import { bulkFetchSamples } from "../../../api/labiq/SampleApi";
import { getLabOrders } from "../../../api/labiq/LabOrdersApi";
import { fetchProtocols } from "../../../api/labiq/ProtocalApi";
import { addItemsToGroup, fetchProcessingGroups } from "../../../api/labiq/GroupProcessingApi";

// Types
import { Sample, SampleAnalysis } from "../../../types/lab/SampleTypes";
import { LabOrder } from "../../../types/lab/LabOrderTypes";
import { Protocol, ProcessingGroup } from "../../../types/lab/ProtocolTypes";

// Import processing components
import SampleProcessingTab from "./processingtabs/SampleProcessingTab";
import SampleListTab from "./processingtabs/SampleListTab";
import AnalysisListTab from "./processingtabs/AnalysisListTab";
import AnalysisProcessingTab from "./processingtabs/AnalysisProcessingTab";

// Utils and hooks
import { useNotifications } from "../../../context/NotificationContext";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";

const SampleProcessingPage: React.FC = () => {
	const [samples, setSamples] = useState<Sample[]>([]);
	const [analyses, setAnalyses] = useState<SampleAnalysis[]>([]);
	const [protocols, setProtocols] = useState<Protocol[]>([]);
	const [pendingGroups, setPendingGroups] = useState<ProcessingGroup[]>([]);
	const [processingOrders, setProcessingOrders] = useState<LabOrder[]>([]);
	const [assigningToGroup, setAssigningToGroup] = useState(false);

	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("1");

	const PER_PAGE = loadUserPreferences().tableRowsPerPage;

	const { showError, showSuccess } = useNotifications();

	useEffect(() => {
		loadProcessingData();
	}, []);

	const loadProcessingData = async () => {
		try {
			setLoading(true);

			const [allOrders, protocolsResponse, pendingGroupsResponse] = await Promise.all([getLabOrders(''), fetchProtocols({ active_only: true }), fetchProcessingGroups({ status: "pending", per_page: PER_PAGE })]);

			setProtocols(protocolsResponse.data);
			setPendingGroups(pendingGroupsResponse.data);

			const filteredOrders = allOrders.filter((order: LabOrder) => ["ready_for_processing", "processing", "results_ready"].includes(order.status));
			setProcessingOrders(filteredOrders);

			const sampleIdsSet = new Set<string>();

			for (const order of filteredOrders) {
				if (order.order_items) {
					for (const item of order.order_items) {
						if (item.status === "completed") continue;
						if (item.sample_id && item.sample) {
							sampleIdsSet.add(item.sample_id);
						}
					}
				}
			}

			const sampleIds = Array.from(sampleIdsSet);

			let tmpSamples: Sample[] = [];
			if (sampleIds.length > 0) {
				try {
					const bulkResponse = await bulkFetchSamples(sampleIds, true);
					tmpSamples = bulkResponse.data;

					if (bulkResponse.not_found.length > 0) {
						console.warn(`${bulkResponse.not_found.length} sample(s) not found:`, bulkResponse.not_found);
					}
				} catch (error) {
					console.error("Error bulk fetching samples:", error);
					showError("Failed to load some samples");
				}
			}

			tmpSamples.sort((a, b) => {
				const dateA = new Date(a.created_at).getTime();
				const dateB = new Date(b.created_at).getTime();
				return dateB - dateA;
			});

			setSamples(tmpSamples);

			// Extract all analyses from samples
			let allAnalyses: SampleAnalysis[] = [];
			tmpSamples.forEach((sample) => {
				if (sample.analyses && sample.analyses.length > 0) {
					allAnalyses = allAnalyses.concat(sample.analyses);
				}
			});
			setAnalyses(allAnalyses);
		} catch (error) {
			showError("Failed to load processing data");
			console.error("Processing data load error:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleAssignToGroup = async (groupId: number, itemsToAssign: any[]) => {
		setAssigningToGroup(true);

		try {
			// Determine if we're assigning samples or analyses
			const group = pendingGroups.find((g) => g.id === groupId);
			if (!group) {
				showError("Group not found");
				return;
			}

			const isFirstProtocol = group.protocol?.is_first_protocol;

			// Use the appropriate endpoint based on protocol type
			const itemType = isFirstProtocol ? "sample" : "analysis";
			const itemIds = itemsToAssign.map((item) => (isFirstProtocol ? item.sampleID : item.analysis_id));
			const response = await addItemsToGroup(groupId, itemType, itemIds);

			if (!response) {
				throw new Error("Failed to assign to group");
			}

			showSuccess(`Successfully assigned ${itemsToAssign.length} ${itemType}(s) to group`);
			loadProcessingData();
		} catch (error) {
			showError(`Failed to assign to group: ${error.message}`);
		} finally {
			setAssigningToGroup(false);
		}
	};

	return (
		<Layout>
			<PageContainer
			>
				{loading ? (
					<SectionCard>
						<InlineLoading message="Loading processing data…" minHeight={400} />
					</SectionCard>
				) : (
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								key: "1",
								label: `Samples (${samples.length})`,
								children: (
									<SampleListTab samples={samples} protocols={protocols} pendingGroups={pendingGroups} orders={processingOrders} loading={loading} onRefresh={loadProcessingData} onAssignToGroup={handleAssignToGroup} assigningToGroup={assigningToGroup} />
								),
							},
							{
								key: "2",
								label: "Sample Processing",
								children: (
									<SampleProcessingTab onRefresh={loadProcessingData} loading={loading} />
								),
							},
							{
								key: "3",
								label: `Analyses to Run (${analyses.length})`,
								children: (
									<AnalysisListTab
										analyses={analyses}
										protocols={protocols}
										pendingGroups={pendingGroups}
										loading={loading}
										onRefresh={loadProcessingData}
										onAssignToGroup={handleAssignToGroup}
										assigningToGroup={assigningToGroup}
									/>
								),
							},
							{
								key: "4",
								label: "Analysis Processing",
								children: (
									<AnalysisProcessingTab onRefresh={loadProcessingData} loading={loading} />
								),
							},
						]}
					/>
				)}
			</PageContainer>
		</Layout>
	);
};

export default SampleProcessingPage;
